import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ProcurementWorkspaceDetail } from '../../procurement/workspace/procurement-workspace.types';
import { PRESERVE_HTTP_ERROR } from '../../shared/utils/preserve-http-error.context';

export interface OrderSupplierCancellationPreview {
  orderId: number;
  buyId: number;
  expectedVersion: number;
  quantity: number;
  lines: Array<{
    buyProductId: number;
    quantity: number;
    allocations: Array<{ kind: 'CUSTOMER'; id: number; quantity: number }>;
  }>;
}

@Injectable({ providedIn: 'root' })
export class OrderSupplierCancellationService {
  constructor(private http: HttpClient) {}

  async prepare(orderId: number, orderProductId: number, allocationId: number, buyId: number) {
    const detail = await this.http.get<ProcurementWorkspaceDetail>(
      `${environment.BACKEND}/procurement-workspace/items/buy/${buyId}`,
      { context: new HttpContext().set(PRESERVE_HTTP_ERROR, true) }
    ).toPromise();
    if (!detail?.buy || Number(detail.buy.id) !== buyId) {
      throw new Error('Не удалось загрузить заказ поставщику');
    }
    const allocations = detail.allocations.filter((allocation) =>
      allocation.kind === 'client' && Number(allocation.id) === allocationId &&
      Number(allocation.orderId) === orderId && Number(allocation.orderProductId) === orderProductId &&
      Number(allocation.openQuantity) > 0.0001
    );
    if (!allocations.length) throw new Error('Поставка уже получена или отменена. Обновите обеспечение заказа.');
    const lines: OrderSupplierCancellationPreview['lines'] = allocations.map((allocation) => {
      const line = detail.lines.find((item) => Number(item.id) === Number(allocation.buyProductId));
      const quantity = Number(allocation.openQuantity);
      if (!line || !Number.isFinite(quantity) || quantity > Number(line.remainingQuantity) + 0.0001) {
        throw new Error('Остаток поставки изменился. Проверьте приёмку и сверку заказа поставщику.');
      }
      return {
        buyProductId: Number(line.id), quantity,
        allocations: [{ kind: 'CUSTOMER', id: allocationId, quantity }]
      };
    });
    return {
      orderId, buyId, expectedVersion: Number(detail.buy.workspaceVersion),
      quantity: lines.reduce((sum, line) => sum + line.quantity, 0), lines
    } as OrderSupplierCancellationPreview;
  }

  cancel(preview: OrderSupplierCancellationPreview, reason: string, operationId: string) {
    const normalizedReason = reason.trim();
    if (!normalizedReason || normalizedReason.length > 1000) {
      throw new Error('Укажите причину отмены, до 1000 символов');
    }
    return this.http.post(
      `${environment.BACKEND}/procurement-workspace/buys/${preview.buyId}/cancel-lines`,
      { expectedVersion: preview.expectedVersion, operationId, reason: normalizedReason, lines: preview.lines },
      { context: new HttpContext().set(PRESERVE_HTTP_ERROR, true) }
    );
  }

  operationId() {
    const values = new Uint8Array(16);
    window.crypto.getRandomValues(values);
    values[6] = (values[6] & 0x0f) | 0x40;
    values[8] = (values[8] & 0x3f) | 0x80;
    const hex = Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
}
